import { useCallback } from 'react';
import { pipe } from 'fp-ts/function';
import { AlertTextProps, RichSubstitutes, lt, showErrorAlert, useTimezone } from '@mews/framework';
import { Button, Dialog } from '@optimus-web/core';
import { Reservation, ResourceCategoryAvailability } from '@mews-commander/data-access';
import { primitiveTypeUniq, values } from '@mews/fp';
import { useCurrentEnterpriseId, usePrivilege } from '@mews-commander/core';
import { useResourceCategoriesByServiceId } from '@mews-commander/legacy-entities/src/utils/useResourceCategory';
import {
    getReservationsAssignedCategoryIds,
    getReservationsAssignedCategoryIdsMap,
} from '@mews-commander/legacy-entities/src/ducks/resourceCategoryAssignments';
import { getCurrentEnterpriseReservationsByIds } from '@mews-commander/legacy-entities/src/selectors/reservations';
import { useService } from '@mews-commander/legacy-entities/src/utils/useService';
import {
    BookableService,
    getServiceOperationalInterval,
    isBookableService,
} from '@mews-commander/legacy-entities/src/ducks/servicesNew';
import { useCommanderSelector } from '@mews-commander/utils';
import { splitAvailabilitiesByOverbooking } from '../../availability/utils/resourceCategoryAvailability';

export enum ReservationChangeValidationState {
    Success = 'Success',
    NoRightsToOverbook = 'NoRightsToOverbook',
    GeneralOverbooking = 'GeneralOverbooking',
    UpdatingAvailabilityBlock = 'UpdatingAvailabilityBlock',
    OverbookingAndUpdatingAvailabilityBlock = 'OverbookingAndUpdatingAvailabilityBlock',
}

interface CustomMessage {
    title?: JSX.Element;
    description?: JSX.Element;
}

type CustomMessages = Record<ReservationChangeValidationState, CustomMessage>;

export interface ValidateReservationChangesResult {
    dialog?: React.ReactNode;
    state: ReservationChangeValidationState;
}

const getDefaultMessage = (
    state: Exclude<ReservationChangeValidationState, ReservationChangeValidationState.Success>,
    substitutes: RichSubstitutes
): CustomMessage =>
    ({
        [ReservationChangeValidationState.NoRightsToOverbook]: {
            description: lt('ReservationDetailOverbookingNoRightsDescription', substitutes),
        },
        [ReservationChangeValidationState.GeneralOverbooking]: {
            title: lt('GeneralOverbookingDialogTitle'),
            description: lt('GeneralOverbookingDialogDescription', substitutes),
        },
        [ReservationChangeValidationState.OverbookingAndUpdatingAvailabilityBlock]: {
            title: lt('OverbookCategoryAndUpdateBlockDialogTitle'),
        },
        [ReservationChangeValidationState.UpdatingAvailabilityBlock]: {
            title: lt('ReservationDetailUpdateBlockDialogTitle'),
        },
    })[state];

const DIALOG_STATES = [
    ReservationChangeValidationState.GeneralOverbooking,
    ReservationChangeValidationState.OverbookingAndUpdatingAvailabilityBlock,
    ReservationChangeValidationState.UpdatingAvailabilityBlock,
];

interface ValidationParams {
    onSubmit: () => void;
    onReject: () => void;
    getCustomMessages?: (
        splitAvailabilities: ReturnType<typeof splitAvailabilitiesByOverbooking>
    ) => Partial<CustomMessages>;
    newResourceCategoryId?: string;
    reservationsCount?: number;
    availabilities: ResourceCategoryAvailability[];
    availabilityBlockId?: string | null;
    /**
     * true if reservations should be omitted from overbooking checks
     */
    omitCurrentReservations?: boolean;
}

export const useValidateReservationChanges = (
    serviceId: string,
    reservationIds: string[]
): ((prams: ValidationParams) => ValidateReservationChangesResult) => {
    const canOverbook = usePrivilege('overbookServices');

    const resourceCategories = useResourceCategoriesByServiceId(serviceId);

    const enterpriseId = useCurrentEnterpriseId();

    const assignedCategoryIdsMap = useCommanderSelector((state) =>
        getReservationsAssignedCategoryIdsMap(state, { reservationIds, enterpriseId })
    );
    const assignedCategoryIds = useCommanderSelector((state) =>
        getReservationsAssignedCategoryIds(state, { reservationIds, enterpriseId })
    );

    const reservations = useCommanderSelector((state) =>
        getCurrentEnterpriseReservationsByIds(state, { ids: reservationIds })
    );

    const service = useService(serviceId);

    const timezone = useTimezone();

    return useCallback(
        ({
            onSubmit,
            onReject,
            newResourceCategoryId,
            reservationsCount,
            getCustomMessages,
            availabilityBlockId,
            availabilities,
            omitCurrentReservations,
        }) => {
            let state = ReservationChangeValidationState.Success;
            let resourceCategoryIdToBeOverbooked = newResourceCategoryId;
            let customMessages: Partial<CustomMessages> = {};

            const uniqResourceCategoryIds = newResourceCategoryId ? [newResourceCategoryId] : assignedCategoryIds;

            for (const categoryId of uniqResourceCategoryIds) {
                const reservationCount =
                    reservationsCount || values(assignedCategoryIdsMap).filter((value) => value === categoryId).length;

                const splitAvailabilities = splitAvailabilitiesByOverbooking(
                    !omitCurrentReservations || !service || !isBookableService(service)
                        ? availabilities
                        : modifyAvailabilities(reservations, assignedCategoryIdsMap, availabilities, timezone, service),
                    categoryId,
                    reservationCount,
                    availabilityBlockId ?? undefined
                );

                const {
                    blockOverbookingAvailabilities,
                    propertyOverbookingAvailabilities,
                    blockAvailableAvailabilities,
                } = splitAvailabilities;

                if (!availabilityBlockId) {
                    if (!propertyOverbookingAvailabilities.length) {
                        state = ReservationChangeValidationState.Success;
                    } else if (!canOverbook) {
                        state = ReservationChangeValidationState.NoRightsToOverbook;
                    } else {
                        state = ReservationChangeValidationState.GeneralOverbooking;
                        resourceCategoryIdToBeOverbooked = categoryId;
                    }
                } else if (!blockOverbookingAvailabilities.length && blockAvailableAvailabilities.length) {
                    state = ReservationChangeValidationState.Success;
                } else if (!propertyOverbookingAvailabilities.length) {
                    state = ReservationChangeValidationState.UpdatingAvailabilityBlock;
                } else if (!canOverbook) {
                    state = ReservationChangeValidationState.NoRightsToOverbook;
                } else {
                    state = ReservationChangeValidationState.OverbookingAndUpdatingAvailabilityBlock;
                    resourceCategoryIdToBeOverbooked = categoryId;
                }

                if (state !== ReservationChangeValidationState.Success) {
                    customMessages = getCustomMessages?.(splitAvailabilities) ?? {};
                    break;
                }
            }

            if (state === ReservationChangeValidationState.Success) {
                onSubmit();
                return { state };
            }

            const substitutes = {
                spaceCategory:
                    (resourceCategoryIdToBeOverbooked && resourceCategories[resourceCategoryIdToBeOverbooked]?.name) ??
                    '',
            };

            const { title, description } = {
                ...getDefaultMessage(state, substitutes),
                ...customMessages[state],
            };

            if (!DIALOG_STATES.includes(state)) {
                if (title || description) {
                    showErrorAlert({ title, description } as AlertTextProps);
                }
                return { state };
            }

            return {
                state,
                dialog: (
                    <Dialog
                        active
                        title={title}
                        onClose={onReject}
                        footer={{
                            secondary: (
                                <Button onClick={onReject} type="submit">
                                    {lt('GoBack')}
                                </Button>
                            ),
                            primary: (
                                <Button variant="primary" onClick={onSubmit} type="submit">
                                    {lt('Save')}
                                </Button>
                            ),
                        }}
                    >
                        {description}
                    </Dialog>
                ),
            };
        },
        [assignedCategoryIds, resourceCategories, reservations, canOverbook, timezone, assignedCategoryIdsMap, service]
    );
};

/**
 * Because of /getAvailabilities endpoint returns 0 for the original reservation interval if category was fully booked there
 * this function will add "fake" availabilities so it looked like hotel has suitable availability for those fully booked days.
 */
const modifyAvailabilities = (
    reservations: Reservation[],
    assignedCategoryIdsMap: Record<string, string>,
    availabilities: ResourceCategoryAvailability[],
    timezone: string,
    service: BookableService
): ResourceCategoryAvailability[] => {
    const assignedCategoryIds = pipe(assignedCategoryIdsMap, values, primitiveTypeUniq);

    return availabilities.map((availability) => {
        // only for category
        if (assignedCategoryIds.includes(availability.resourceCategoryId)) {
            const fitReservations = reservations.filter((r) => {
                if (assignedCategoryIdsMap[r.id] !== availability.resourceCategoryId) {
                    return false;
                }

                const { firstTimeUnitStartUtc, lastTimeUnitStartUtc } = getServiceOperationalInterval(
                    r,
                    service,
                    timezone
                );

                return (
                    firstTimeUnitStartUtc <= availability.timeUnitBaseStartUtc &&
                    availability.timeUnitBaseStartUtc <= lastTimeUnitStartUtc
                );
            }).length;

            if (fitReservations > 0 && availability.value < fitReservations) {
                availability.value += fitReservations - availability.value;
            }
        }

        return availability;
    });
};
